import { Injectable, Injector, inject } from '@angular/core';
import { ApiClientService } from '@xbh/common-lib';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { CommonConstants } from '../../pages/common/constants/common.constants';
import { resolveSecurityNumberApiParts } from '../../pages/common/utils/format-security-number';
import { InsuranceCardHeaderData } from '../../shared/insurance-card-header/insurance-card-header.component';
import { MasterDataStore } from '../services/master-data.store';
import { RegistUsageHistoryRequest } from './usage-history.entity';

@Injectable({
  providedIn: 'root',
})
export class DataCacheService {
  private readonly STORAGE_KEY = 'secure_biz_data';

  private readonly TEMP_DATA_KEY = 'temp_data';

  private readonly FROM_MPLOG_KEY = 'from_mplog';

  private readonly CUSTOMER_NUMBER_KEY = 'customer_number';

  private readonly KOJIN_NUMBER_KEY = 'kojin_number';

  private readonly apiClient = inject(ApiClientService);

  /** MasterDataStore との循環参照を避けるため、利用時に解決する */
  private readonly injector = inject(Injector);

  // 一時保存用のデータを更新するメソッド（必要に応じて外部から呼び出せるようにするため）
  initTempData(): Observable<any> {
    if (this.ifFromMplog()) {
      return of(null);
    }
    const hasTempData = !!sessionStorage.getItem(this.TEMP_DATA_KEY);
    if (hasTempData) {
      return of(null);
    }

    const tempDataGetUrl = `${environment.bffUrl}/${environment.apiVersion}/api/tempDataGet`;

    const url = new URL(window.location.href);
    const keyFromUrl = url.searchParams.get('key') ?? '';

    const preAuthBody = {
      // 契約者マイページを表示する際のリクエストパラメータに含まれる項目名「key」の値
      key: keyFromUrl,
    };

    const userId = environment.isInternal ? '' : 'mp';
    // エミュレーションモード：JV1MHP0101
    // エミュレーションモード以外：JV1MHP0901
    const userGroup1 = this.getEmulationSign() === '1' ? 'JV1MHP0101' : 'JV1MHP0901';

    return this.apiClient.request<any>(tempDataGetUrl, 'post', userId, userGroup1, '', preAuthBody).pipe(
      tap((response) => {
        sessionStorage.setItem(this.TEMP_DATA_KEY, response?.data ? JSON.stringify(response.data) : '');
        console.log('一時保存データを取得しました:', response?.data);
      }),
      // API呼び出しに失敗した場合はエラーをキャッチして null を返す（これによりアプリの起動を続行できるようにする）
      catchError((err) => {
        return of(null);
      }),
    );
  }

  // データを暗号化してSessionStorageに保存（毎回上書きされる）
  saveBizData(data: any): void {
    if (!data) return;
    const str = JSON.stringify(data);
    const encrypted = window.btoa(encodeURIComponent(str));
    sessionStorage.setItem(this.STORAGE_KEY, encrypted);
  }

  // SessionStorageからデータを取得して復号化
  getBizData(): any {
    const encrypted = sessionStorage.getItem(this.STORAGE_KEY);
    if (!encrypted) return null;
    try {
      const str = decodeURIComponent(window.atob(encrypted));
      return JSON.parse(str);
    } catch (e) {
      return null;
    }
  }

  // mplogから/一時保存取得API に保存された　顧客番号
  getCustomerNumber(): any {
    if (this.ifFromMplog()) {
      return sessionStorage.getItem(this.CUSTOMER_NUMBER_KEY);
    } else {
      const tempDataRaw = sessionStorage.getItem(this.TEMP_DATA_KEY);
      if (!tempDataRaw) return null;
      try {
        return JSON.parse(tempDataRaw)?.customerNumber;
      } catch (e) {
        return null;
      }
    }
  }

  // mplogから/一時保存取得API に保存された　エミュレーションSN
  getEmulationSign(): any {
    if (this.ifFromMplog()) {
      return '1'; // mplog からの遷移の場合は、エミュレーションSNを '1' として返す;
    } else {
      const tempDataRaw = sessionStorage.getItem(this.TEMP_DATA_KEY);
      if (!tempDataRaw) return null;
      try {
        return JSON.parse(tempDataRaw)?.emulationSign;
      } catch (e) {
        return null;
      }
    }
  }

  // mplogから/一時保存取得API に保存された　個人番号
  getKojinNumber(): any {
    if (this.ifFromMplog()) {
      return sessionStorage.getItem(this.KOJIN_NUMBER_KEY);
    } else {
      const tempDataRaw = sessionStorage.getItem(this.TEMP_DATA_KEY);
      if (!tempDataRaw) return null;
      try {
        return JSON.parse(tempDataRaw)?.kojinNumber;
      } catch (e) {
        return null;
      }
    }
  }

  // mplog からの遷移であることを示すフラグ
  ifFromMplog(): any {
    const tempDataRaw = sessionStorage.getItem(this.FROM_MPLOG_KEY);
    if (tempDataRaw) {
      return true;
    }
    const currentUrl = window.location.href;
    if (currentUrl.includes('businessScreenId=')) {
      const urlObj = new URL(currentUrl);
      const params = urlObj.searchParams;
      const customerNumber = params.get('customerNumber');
      const kojinNumber = params.get('kojinNumber');

      sessionStorage.setItem(this.FROM_MPLOG_KEY, 'true');
      sessionStorage.setItem(this.CUSTOMER_NUMBER_KEY, customerNumber || '');
      sessionStorage.setItem(this.KOJIN_NUMBER_KEY, kojinNumber || '');
      return true;
    }
    return false;
  }

  // ★今回の設計では、通常の画面遷移中はこのメソッドを呼び出しません！
  // タブが閉じられる時にブラウザが自動でクリアします。
  clearBizData(): void {
    sessionStorage.removeItem(this.STORAGE_KEY);
  }

  /**
   * 利用履歴登録API (MP1000) を呼び出す（POST方式）
   *
   * @param request 登録リクエスト
   * @returns 登録処理の Observable
   */
  public registUsageHistory(request: RegistUsageHistoryRequest): Observable<any> {
    const url = `${environment.bffUrl}/mp/${environment.apiVersion}/api/mp1000`;
    const body: RegistUsageHistoryRequest = {
      customerNumber: request.customerNumber, // 顧客番号
      kojinNumber: request.kojinNumber, // 個人番号
      receptionDatetime: this.nowAsHostFormat(), // 処理受付日時
      businessScreenId: request.businessScreenId, // 業務画面ID
      status: request.status, // ステータス
      policyNumber: request.policyNumber, // 証券番号
      progressNumber: request.progressNumber, // 進捗番号
      transitionSourceId: request.transitionSourceId, // 遷移元画面ID
      transitionDestinationId: request.transitionDestinationId, // 遷移先画面ID
      errorPolicyNumber: request.errorPolicyNumber, // エラー証券番号
      errorCode: request.errorCode, // エラーコード
      errorScreenId: request.errorScreenId, // エラー画面ID
      businessData: this.getBizData(), // 業務データ this.getBizData(),
    };
    return this.apiClient.request<void>(url, 'post', 'mp', 'MP_GROUP1', '', body);
  }

  /**
   * MP0051 失敗により MP0001 を呼べない場合の利用履歴を 1 件登録する。
   * MP0051 成功時は登録せず、MP0001 完了時に MP0051 とまとめて 1 件登録する。
   * エミュレーション／利用履歴再生時、顧客番号未取得時は登録しない。
   */
  public registInitDataUnavailableUsageHistory(): void {
    if (this.getEmulationSign() === '1' || this.ifFromMplog()) {
      return;
    }
    const customerNumber = this.getCustomerNumber();
    if (!customerNumber) {
      return;
    }
    this.registUsageHistory({
      customerNumber,
      kojinNumber: this.getKojinNumber() ?? '',
      receptionDatetime: '',
      businessScreenId: CommonConstants.MP0010,
      status: CommonConstants.STATUS_ERROR,
      policyNumber: '',
      progressNumber: '',
      transitionSourceId: CommonConstants.MP0000,
      transitionDestinationId: CommonConstants.MP0010,
      errorPolicyNumber: '',
      errorCode: '',
      errorScreenId: CommonConstants.MP9010,
    }).subscribe();
  }

  /**
   * 処理受付日時をホスト形式（yyyyMMddHHmmssSSS）で取得する。
   * 日付はマスタデータ取得APIのシステム日付、時刻は端末時刻を使用する。
   *
   * @returns ホスト形式の日時文字列
   */
  private nowAsHostFormat(): string {
    const systemDate = this.injector.get(MasterDataStore).getSystemDate();
    const now = new Date();
    const pad = (value: number, length = 2): string => String(value).padStart(length, '0');
    return `${systemDate.getFullYear()}${pad(systemDate.getMonth() + 1)}${pad(systemDate.getDate())}` + `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}${pad(now.getMilliseconds(), 3)}`;
  }

  saveDegiNo(card: InsuranceCardHeaderData): void {
    const securityNumberParts = resolveSecurityNumberApiParts(card);
    if (!securityNumberParts.degiSknNo) {
      return;
    }
    sessionStorage.setItem('degiSknNo', securityNumberParts.degiSknNo); // 証券－番号
    sessionStorage.setItem('degiShsNoKoNo', securityNumberParts.degiShsNoKoNo); // 証書番号子－番号
    sessionStorage.setItem('degiKyNoCkdNo', securityNumberParts.degiKyNoCkdNo); // 契約番号ＣＫＤ－番号
    sessionStorage.setItem('productName', card?.productName ?? ''); // 商品名称
  }
}
